import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';

const Termos = () => {
  const navigate = useNavigate();
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    document.title = 'Termos de Uso - Ateliê PRO';
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-purple-900">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-purple-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center">
          <button onClick={() => navigate(-1)} className="text-purple-600 hover:text-purple-700 font-medium">
            ← Voltar
          </button>
          <span className="text-xs text-gray-500">v4 • 2026-05-07</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center text-white mb-12">
          <h1 className="text-5xl font-bold mb-2">Termos de Uso</h1>
          <p className="text-xl text-purple-200">Ateliê PRO</p>
          <p className="text-sm text-gray-300 mt-4">Última atualização: 7 de maio de 2026 | Versão: 2026-05-07-v4</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-2xl p-12 space-y-8 text-gray-900">
          {/* Intro */}
          <div className="border-l-4 border-purple-500 pl-6 py-4">
            <p className="text-lg leading-relaxed">
              Ao utilizar a plataforma Ateliê PRO, você declara estar ciente e de acordo com estes Termos de Uso. Caso não concorde com qualquer disposição, não utilize a plataforma.
            </p>
          </div>

          <hr className="border-gray-200" />

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Definições</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>Plataforma:</strong> sistema Ateliê PRO, incluindo website, painel administrativo, integrações, APIs e funcionalidades.</p>
              <p><strong>Usuário:</strong> pessoa física ou jurídica responsável pela conta cadastrada.</p>
              <p><strong>Serviços:</strong> funcionalidades disponibilizadas pelo Ateliê PRO.</p>
              <p><strong>Dados:</strong> informações inseridas pelo Usuário na Plataforma.</p>
              <p><strong>Parceiros:</strong> empresas terceiras utilizadas para funcionamento da Plataforma, incluindo serviços de hospedagem, pagamentos e emissão fiscal.</p>
            </div>
          </section>

          <hr className="border-gray-200" />

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Natureza do Serviço</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Ateliê PRO é disponibilizado no modelo SaaS ("Software as a Service"), em ambiente online e em evolução contínua.</p>
              <p className="font-semibold">A Plataforma poderá receber:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>atualizações;</li>
                <li>melhorias;</li>
                <li>correções;</li>
                <li>alterações de interface;</li>
                <li>novas funcionalidades;</li>
                <li>remoção de recursos;</li>
                <li>manutenções preventivas ou corretivas.</li>
              </ul>
              <p>Algumas atualizações poderão ocorrer sem aviso prévio individual.</p>
            </div>
          </section>

          <hr className="border-gray-200" />

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Disponibilidade e Funcionamento</h2>
            <div className="space-y-3 text-gray-700">
              <p>Embora o Ateliê PRO busque manter a Plataforma disponível de forma contínua, poderão ocorrer:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>indisponibilidades temporárias;</li>
                <li>falhas técnicas;</li>
                <li>lentidão;</li>
                <li>interrupções;</li>
                <li>manutenção programada;</li>
                <li>falhas de terceiros;</li>
                <li>eventos de força maior.</li>
              </ul>
              <p>Nenhum sistema é completamente livre de falhas ou interrupções.</p>
              <p>Falhas pontuais, instabilidades temporárias ou comportamentos inesperados da Plataforma não geram automaticamente direito a reembolso ou indenização, sendo cada situação analisada individualmente.</p>
            </div>
          </section>

          <hr className="border-gray-200" />

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Uso Permitido</h2>
            <div className="space-y-3 text-gray-700">
              <p>Você poderá utilizar a Plataforma exclusivamente para fins legítimos relacionados à gestão do seu negócio.</p>
              <p className="font-semibold">Você concorda em:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Utilizar informações verdadeiras</li>
                <li>Manter seus dados atualizados</li>
                <li>Respeitar a legislação aplicável</li>
                <li>Não utilizar a Plataforma de forma abusiva ou ilícita</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Proibições</h2>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold">É proibido:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Realizar engenharia reversa</li>
                <li>Copiar, clonar ou reproduzir a Plataforma</li>
                <li>Utilizar scraping, bots ou automações não autorizadas</li>
                <li>Compartilhar credenciais de acesso</li>
                <li>Explorar vulnerabilidades</li>
                <li>Acessar áreas não autorizadas</li>
                <li>Utilizar a Plataforma para fins ilegais</li>
                <li>Transmitir malware ou conteúdo malicioso</li>
                <li>Utilizar a Plataforma para concorrência desleal</li>
                <li>Comercializar acessos sem autorização</li>
              </ul>
              <p className="font-semibold">Atividades abusivas poderão resultar em:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Limitação de recursos</li>
                <li>Suspensão temporária</li>
                <li>Encerramento da conta</li>
                <li>Adoção de medidas legais cabíveis</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Responsabilidade sobre Dados</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Usuário é integralmente responsável:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Pelos dados inseridos</li>
                <li>Pela conferência das informações</li>
                <li>Pela legitimidade dos dados utilizados</li>
                <li>Pela base legal de tratamento de dados de terceiros</li>
                <li>Pela conformidade fiscal e tributária de suas operações</li>
              </ul>
              <p>O Usuário declara possuir autorização adequada para utilização dos dados pessoais inseridos na Plataforma.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Responsabilidades Fiscais</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Ateliê PRO é ferramenta de apoio à gestão e não substitui:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Contador</li>
                <li>Advogado</li>
                <li>Consultor tributário</li>
                <li>Assessoria fiscal</li>
              </ul>
              <p className="font-semibold">O Usuário é exclusivamente responsável:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Pela configuração tributária</li>
                <li>Emissão fiscal</li>
                <li>Conformidade perante a SEFAZ</li>
                <li>Cálculos tributários</li>
                <li>Obrigações legais</li>
                <li>Multas, autuações e penalidades</li>
              </ul>
              <p>Não garantimos aprovação automática de documentos fiscais pelos órgãos competentes.</p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Backups e Segurança</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Ateliê PRO adota medidas técnicas e organizacionais razoáveis de segurança, incluindo:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Criptografia</li>
                <li>Controle de acesso</li>
                <li>Monitoramento</li>
                <li>Backups periódicos</li>
              </ul>
              <p>Apesar disso, nenhum sistema é totalmente imune a falhas ou ataques cibernéticos.</p>
              <p>O Usuário também é responsável por manter cópias próprias dos dados considerados importantes.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Integrações com Terceiros</h2>
            <div className="space-y-3 text-gray-700">
              <p>A Plataforma poderá depender de serviços de terceiros, incluindo:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>ASAAS</li>
                <li>FocusNFe</li>
                <li>Supabase</li>
                <li>Google Analytics</li>
                <li>Serviços governamentais</li>
                <li>Provedores de internet</li>
              </ul>
              <p className="font-semibold">O Ateliê PRO não se responsabiliza por:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Falhas de terceiros</li>
                <li>Indisponibilidade de APIs externas</li>
                <li>Falhas da SEFAZ</li>
                <li>Problemas bancários</li>
                <li>Indisponibilidade de internet</li>
                <li>Interrupções externas</li>
              </ul>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Assinaturas e Cobranças</h2>
            <div className="space-y-3 text-gray-700">
              <p>As assinaturas são recorrentes e renovadas automaticamente conforme periodicidade contratada.</p>
              <p>Ao contratar um plano, o Usuário autoriza cobranças recorrentes automáticas até cancelamento da assinatura.</p>
              <p>O cancelamento poderá ser realizado a qualquer momento pelo painel ou canais oficiais de atendimento.</p>
              <p>O cancelamento interrompe cobranças futuras, mas não gera automaticamente reembolso de valores já pagos.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Política de Reembolso</h2>
            <div className="space-y-3 text-gray-700">
              <p>Solicitações de reembolso serão analisadas individualmente.</p>
              <p>O Usuário poderá exercer direito de arrependimento nos termos da legislação aplicável.</p>
              <p className="font-semibold">O Ateliê PRO poderá recusar reembolsos em casos de:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Fraude</li>
                <li>Abuso</li>
                <li>Má-fé</li>
                <li>Uso excessivo da Plataforma</li>
                <li>Violação destes Termos</li>
                <li>Tentativa de obtenção indevida de benefício financeiro</li>
              </ul>
              <p>Falhas pontuais, indisponibilidades temporárias, manutenção, ajustes operacionais ou limitações técnicas não geram automaticamente direito a reembolso.</p>
              <p>Reembolsos aprovados seguirão os prazos e regras das instituições financeiras e meios de pagamento utilizados.</p>
            </div>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Chargebacks e Disputas</h2>
            <div className="space-y-3 text-gray-700">
              <p>Antes de abrir disputas financeiras ou chargebacks, o Usuário deverá buscar resolução pelos canais oficiais do Ateliê PRO.</p>
              <p>Poderemos suspender temporariamente a conta durante análise de disputas financeiras, fraudes ou contestações.</p>
            </div>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Suporte Técnico</h2>
            <div className="space-y-3 text-gray-700">
              <p>O suporte é oferecido conforme disponibilidade operacional e plano contratado.</p>
              <p className="font-semibold">O suporte cobre:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Dúvidas técnicas</li>
                <li>Problemas de funcionamento</li>
                <li>Orientações de uso da Plataforma</li>
              </ul>
              <p className="font-semibold">O suporte não inclui:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Consultoria tributária</li>
                <li>Assessoria jurídica</li>
                <li>Serviços contábeis</li>
                <li>Desenvolvimento personalizado</li>
              </ul>
            </div>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Limitação de Responsabilidade</h2>
            <div className="space-y-3 text-gray-700">
              <p>A responsabilidade do Ateliê PRO observará os limites permitidos pela legislação aplicável.</p>
              <p className="font-semibold">Em nenhuma hipótese o Ateliê PRO será responsável por:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Lucros cessantes</li>
                <li>Perda de faturamento</li>
                <li>Danos indiretos</li>
                <li>Perda de oportunidade</li>
                <li>Danos reputacionais</li>
                <li>Prejuízos decorrentes de terceiros</li>
                <li>Decisões tomadas pelo Usuário com base na Plataforma</li>
              </ul>
              <p>A responsabilidade financeira total do Ateliê PRO fica limitada ao valor efetivamente pago pelo Usuário nos últimos 12 meses anteriores ao evento que originou a reclamação.</p>
            </div>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Segurança da Conta</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Usuário é responsável por:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Manter senha segura</li>
                <li>Proteger suas credenciais</li>
                <li>Impedir acesso não autorizado</li>
                <li>Informar imediatamente qualquer suspeita de invasão</li>
              </ul>
              <p>Atividades suspeitas poderão resultar em medidas preventivas de segurança.</p>
            </div>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Propriedade Intelectual</h2>
            <div className="space-y-3 text-gray-700">
              <p>Todo conteúdo, código, identidade visual, funcionalidades, marcas e tecnologia do Ateliê PRO pertencem exclusivamente à Plataforma e são protegidos pela legislação aplicável.</p>
              <p>O Usuário não adquire qualquer direito de propriedade intelectual sobre a Plataforma.</p>
            </div>
          </section>

          {/* Section 17 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Funcionalidades Experimentais</h2>
            <div className="space-y-3 text-gray-700">
              <p>Recursos identificados como beta, experimental ou em testes poderão apresentar instabilidades superiores às funcionalidades estáveis.</p>
            </div>
          </section>

          {/* Section 18 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Resultados Não Garantidos</h2>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold">O Ateliê PRO não garante:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Aumento de faturamento</li>
                <li>Crescimento de vendas</li>
                <li>Organização financeira</li>
                <li>Melhoria operacional</li>
                <li>Qualquer resultado comercial específico</li>
              </ul>
            </div>
          </section>

          {/* Section 19 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Alterações dos Termos</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Ateliê PRO poderá modificar estes Termos periodicamente.</p>
              <p>Alterações relevantes poderão ser comunicadas por email, notificações na plataforma ou outros canais oficiais.</p>
              <p>A continuidade de uso da Plataforma após atualização constitui concordância com a nova versão.</p>
            </div>
          </section>

          {/* Section 20 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Legislação Aplicável</h2>
            <div className="space-y-3 text-gray-700">
              <p>Estes Termos são regidos pelas leis da República Federativa do Brasil.</p>
              <p>Eventuais disputas observarão o foro competente conforme legislação aplicável ao consumidor.</p>
            </div>
          </section>

          {/* Section 21 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">21. Disposições Gerais</h2>
            <div className="space-y-3 text-gray-700">
              <p>A eventual invalidade de uma cláusula não afetará as demais disposições destes Termos.</p>
              <p>A ausência de exercício imediato de qualquer direito não constitui renúncia.</p>
              <p>O presente documento constitui o acordo integral entre as partes.</p>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-8 rounded-lg text-center">
            <p className="font-semibold mb-2">Concorda com nossos Termos?</p>
            <p className="text-sm text-purple-100">Ao utilizar a plataforma, você aceita automaticamente estes termos.</p>
          </div>
        </div>
      </div>

      {/* Scroll to Top */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default Termos;